import apollo from "@/apollo.js"
import gql from "graphql-tag"

export const setScriptEngineAttrs = async (data, llmApiKey) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation setScriptEngineAttrs($attrs: JSON, $llmApiKey: String) {
            setScriptEngineAttrs(attrs: $attrs, llmApiKey: $llmApiKey) {
              error
              message
              lowLevelErrorMessage
              account {
                id
                title
                nickname
                timezone
                adminConsoleToken
                chatBriefing
                chatReports
                chatMemoryEnabled
                scriptEngineAttrs
                llmApiKeyConfigured
              }
            }
        }
    `),
    variables: {
      attrs: data,
      llmApiKey: llmApiKey && String(llmApiKey).trim() ? String(llmApiKey).trim() : null,
    },
  })
  return response.data.setScriptEngineAttrs
}

export const setDefaultTeam = async (teamId) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation setDefaultTeam($teamId: Int!) {
          setDefaultTeam(teamId: $teamId) {
            account {
              id
              title
              nickname
              timezone
              defaultTeam { id displayName }
            }
            error
            message
            lowLevelErrorMessage
          }
        }`),
    variables: { teamId },
  })
  return response.data.setDefaultTeam
}

export const setAccountTimezone = async (timezone) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation setAccountTimezone($timezone: String!) {
          setAccountTimezone(timezone: $timezone) {
            account {
              id
              title
              nickname
              timezone
              adminConsoleToken
              chatBriefing
              chatReports
              scriptEngineAttrs
            }
            error
            message
            lowLevelErrorMessage
          }
        }`),
    variables: { timezone },
  })
  return response.data.setAccountTimezone
}

export const setAccountChatBriefing = async (chatBriefing) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation setAccountChatBriefing($chatBriefing: String!) {
          setAccountChatBriefing(chatBriefing: $chatBriefing) {
            account {
              id
              title
              nickname
              timezone
              adminConsoleToken
              chatBriefing
              chatReports
              scriptEngineAttrs
            }
            error
            message
            lowLevelErrorMessage
          }
        }`),
    variables: { chatBriefing },
  })
  return response.data.setAccountChatBriefing
}

export const setAccountChatReports = async (chatReports) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation setAccountChatReports($chatReports: JSON!) {
          setAccountChatReports(chatReports: $chatReports) {
            account {
              id
              title
              nickname
              timezone
              adminConsoleToken
              chatBriefing
              chatReports
              scriptEngineAttrs
            }
            error
            message
            lowLevelErrorMessage
          }
        }`),
    variables: { chatReports },
  })
  return response.data.setAccountChatReports
}

export const accountFeatureToggles = async () => {
  const response = await apollo.query({
    query: gql(`
      query accountFeatureToggles {
        currentUser {
          account {
            featureToggles {
              code
              title
              description
              enabled
              attrs
            }
          }
        }
      }
    `),
    fetchPolicy: "network-only",
  })
  const currentUser = response.data && response.data.currentUser
  const account = currentUser && currentUser.account
  return (account && account.featureToggles) || []
}

export const setAccountFeature = async ({ code, enabled, attrs }) => {
  const variables = { code, enabled, attrs: attrs === undefined ? null : attrs }
  const response = await apollo.mutate({
    mutation: gql(`
      mutation setAccountFeature($code: String!, $enabled: Boolean!, $attrs: JSON) {
        setAccountFeature(code: $code, enabled: $enabled, attrs: $attrs) {
          error
          message
          lowLevelErrorMessage
        }
      }
    `),
    variables: variables,
  })
  return response.data.setAccountFeature
}

export const setChatMemoryEnabled = async (enabled) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation setChatMemoryEnabled($enabled: Boolean!) {
          setChatMemoryEnabled(enabled: $enabled) {
            account {
              id
              title
              nickname
              timezone
              adminConsoleToken
              chatBriefing
              chatReports
              chatMemoryEnabled
              scriptEngineAttrs
            }
            error
            message
            lowLevelErrorMessage
          }
        }`),
    variables: { enabled },
  })
  return response.data.setChatMemoryEnabled
}

export const setAccountRefreshTokenExpiryHours = async ({ id, refreshTokenExpiryHours }) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation updateAccountRefreshTokenExpiry($id: Int!, $refreshTokenExpiryHours: Int) {
          updateAccount(id: $id, refreshTokenExpiryHours: $refreshTokenExpiryHours) {
            account {
              id
              title
              nickname
              timezone
              adminConsoleToken
              chatBriefing
              chatReports
              chatMemoryEnabled
              refreshTokenExpiryHours
              scriptEngineAttrs
            }
            error
            message
            lowLevelErrorMessage
          }
        }`),
    variables: { id, refreshTokenExpiryHours },
  })
  return response.data.updateAccount
}

export const setAccountAdminConsoleToken = async ({ id, adminConsoleToken }) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation updateAccountAdminConsoleToken($id: Int!, $adminConsoleToken: String) {
          updateAccount(id: $id, adminConsoleToken: $adminConsoleToken) {
            account {
              id
              title
              nickname
              timezone
              chatBriefing
              chatReports
              chatMemoryEnabled
              refreshTokenExpiryHours
              adminConsoleToken
              scriptEngineAttrs
            }
            error
            message
            lowLevelErrorMessage
          }
        }`),
    variables: { id, adminConsoleToken },
  })
  return response.data.updateAccount
}

export const setOidcSso = async (config) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation setOidcSso($config: OidcSsoInput!) {
          setOidcSso(config: $config) {
            account {
              id
              title
              nickname
              timezone
              adminConsoleToken
              chatBriefing
              chatReports
              chatMemoryEnabled
              refreshTokenExpiryHours
              scriptEngineAttrs
              llmApiKeyConfigured
              oidcSso {
                enabled
                issuer
                clientId
                clientSecretConfigured
                buttonLabel
                scopes
                redirectUri
                autoCreateUsers
                defaultTeam
                disablePasswordLogin
              }
            }
            error
            message
            lowLevelErrorMessage
          }
        }`),
    variables: { config },
  })
  return response.data.setOidcSso
}

export const performReindex = async (data) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation reindexAllCaseTypes {
          reindexAllCaseTypes(dropExistingIndex: true) {
            message
            error
          }
        }
      `),
    variables: {},
  })
  return response.data.reindexAllCaseTypes
}
